// src/lib/auth/apple.ts
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';

// === Constants for Apple Sign-In standardization ===
export const APPLE_SERVICE_ID = 'org.prayai.app.web';          // Service ID (client_id)
export const IOS_BUNDLE_ID    = 'org.prayai.app';                  // iOS bundle id
export const APPLE_REDIRECT   = 'https://daeuiiggqdxyseiekbca.supabase.co/auth/v1/callback'; // per Supabase docs

// Simple nonce generator (good enough for SIWA)
function randomNonce(len = 32) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = ''; 
  for (let i = 0; i < len; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

// SHA-256 hash for Apple nonce requirement
async function sha256Hex(input: string): Promise<string> {
  try {
    const data = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Fallback if crypto.subtle unavailable
    console.warn('[Apple Auth] crypto.subtle unavailable; using raw nonce (not ideal).');
    return input;
  }
}

/**
 * Web OAuth (browser / PWA / desktop): Apple via Service ID + redirectTo
 * Requires you to have configured:
 *  - Service ID = org.prayai.app.web
 *  - Return URL = https://daeuiiggqdxyseiekbca.supabase.co/auth/v1/callback
 *  - Supabase Apple provider with client secret (your eyJ… JWT)
 */
export async function signInWithAppleWeb() {
  console.log('[Apple Auth] Starting web Apple Sign-In');
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: APPLE_REDIRECT,
    },
  });
  
  if (error) {
    console.error('[Apple Auth] Web OAuth error:', error);
    // Provide more helpful error messages for common issues
    if (error.message?.includes('invalid_client_id') || error.message?.includes('invalid_client')) {
      throw new Error('Apple authentication configuration error. The Apple client secret may have expired. Please contact support.');
    }
    throw error;
  }
  
  console.log('[Apple Auth] Web OAuth initiated successfully');
  return data;
}

/**
 * Native iOS: use the Apple native flow to get an identityToken, then hand it to Supabase.
 * This uses @capacitor-community/apple-sign-in. If the plugin isn't installed,
 * we gracefully fall back to the web OAuth flow above.
 */
export async function signInWithAppleNative() {
  const isIOSNative = Capacitor.getPlatform() === 'ios' && (Capacitor.isNativePlatform?.() ?? true);
  if (!isIOSNative) return signInWithAppleWeb();

  const rawNonce = randomNonce(32);
  const hashedNonce = await sha256Hex(rawNonce);

  try {
    // Import at runtime to avoid bundling in web
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

    console.log('[Apple Auth] Starting native Apple Sign-In with config:', {
      clientId: APPLE_SERVICE_ID,
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform()
    });

// ... in signInWithAppleNative()

const result = await SignInWithApple.authorize({
  clientId: IOS_BUNDLE_ID, // <-- Use Bundle ID for native iOS
  redirectURI: APPLE_REDIRECT,
  scopes: 'name email',
  state: hashedNonce,
  nonce: hashedNonce,
} as any);

    console.log('[Apple Auth] Received authorization result:', { 
      hasResponse: !!result?.response,
      hasToken: !!result?.response?.identityToken
    });

    const identityToken = result?.response?.identityToken;
    if (!identityToken) {
      throw new Error('No identityToken from Apple. This may indicate: 1) Simulator without iCloud sign-in, 2) Expired Apple certificates, or 3) Apple Developer account issues.');
    }

    // Now tell Supabase to trust this Apple token
    console.log('[Apple Auth] Exchanging Apple token with Supabase...');
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: identityToken,
      nonce: rawNonce, // Supabase expects raw nonce
    });
    
    if (error) {
      console.error('[Apple Auth] Supabase token exchange failed:', error);
      // Provide more specific error messages for common issues
      if (error.message?.includes('invalid_grant') || error.message?.includes('invalid_client')) {
        throw new Error('Apple authentication failed. The Apple client secret may have expired. Please contact support or check Apple Developer Console configuration.');
      }
      throw error;
    }
    
    console.log('[Apple Auth] Successfully authenticated with Supabase');
    return data;

  } catch (err: any) {
    console.error('[Apple Auth] Error during native sign-in:', err);
    
    // Handle user cancellation silently
    if (/cancel|canceled|cancelled/i.test(String(err?.message || err))) {
      console.log('[Apple Auth] User cancelled sign-in');
      return null; // Silent exit on cancel
    }
    
    // If plugin missing, fall back to web OAuth
    if (String(err?.message || err).includes('plugin_not_installed')) {
      console.warn('[Apple Auth] Plugin not available, falling back to web OAuth');
      return signInWithAppleWeb();
    }
    
    // Handle specific Apple authorization errors
    if (String(err?.message || err).includes('1000') || String(err?.message || err).includes('AuthorizationError')) {
      throw new Error('Apple Sign-In configuration error. The Apple client secret may have expired or there may be a configuration issue. Please try again later or contact support.');
    }
    
    throw err;
  }
}

/** One entry point your UI can call */
export async function signInWithAppleSmart() {
  const isIOSNative = Capacitor.getPlatform() === 'ios' && (Capacitor.isNativePlatform?.() ?? true);
  return isIOSNative ? signInWithAppleNative() : signInWithAppleWeb();
}
